import { useState, useEffect, useRef, memo } from 'react';
import type { RefObject } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Heart, HeartCrack } from 'lucide-react';
import { CaptureSlide, WindowCaptureSlide, type CaptureSlideHandle, type WindowCaptureHandle } from './CaptureSlide';
import type { DispositivoVideoCaptura } from '@/shared/lib/hdmiDevices';
import MediaSlide from './MediaSlide';
import YoutubeSlide, { type YoutubeSlideHandle } from './YoutubeSlide';
import AdsSidebar from './AdsSidebar';
import JukeboxQRCode from './JukeboxQRCode';
import { ConnectionDot, PlaylistVaziaScreen, StandbyScreen } from './PlayerAux';
import { TvFutebolOverlay } from '@/features/futebol';
import { TvReacaoOverlay, getClientePrimeiroNome } from '@/features/jukebox';
import TvClienteBadge from './TvClienteBadge';
import TvRaioXBar from './TvRaioXBar';
import { TvPdvDestaqueCard, TvChoppTermometro, type TvChoppTermometroProps } from '@/features/pdv-engajamento';
import { TvResultadoEleicao } from '@/features/eleicao';
import type { Tela, Midia, PlaylistItem, FilaJukebox, FutebolPartidaAtiva, FonteReproducao, PerfilAudio, MusicaRaioX, PdvDestaqueItem, EleicaoSnapshot } from '@/shared/types';
import type { PlayerConfig } from '../hooks/usePlayerConfig';
import type { NotifAtiva } from '../hooks/useNotifQueue';
import { resolverFaixaAtual } from '../../domain/usecases/faixaAtual';

// ── Shadow tokens — inner refraction simula espessura de vidro (Liquid Glass) ──
const SHADOW_NOW_PLAYING      = '0 8px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(0,0,0,0.15)';
const SHADOW_FILA_NOTIF       = '0 8px 32px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.12)';
const SHADOW_BOTAO_VOLTAR     = '0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(0,0,0,0.10)';
const SHADOW_PEDIDO_DEFAULT   = '0 20px 70px rgba(234,88,12,0.6), inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 0 rgba(0,0,0,0.20)';
const SHADOW_PEDIDO_ROMANCE   = '0 20px 70px rgba(190,18,60,0.7), inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 0 rgba(0,0,0,0.20)';
const SHADOW_PEDIDO_SOFRENCIA = '0 20px 70px rgba(14,116,144,0.7), inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 0 rgba(0,0,0,0.20)';

const BG_PEDIDO_ROMANCE   = 'linear-gradient(135deg, rgba(159,18,57,0.97) 0%, rgba(190,18,60,0.97) 50%, rgba(136,19,55,0.97) 100%)';
const BG_PEDIDO_SOFRENCIA = 'linear-gradient(135deg, rgba(14,116,144,0.97) 0%, rgba(29,78,216,0.97) 50%, rgba(15,23,42,0.97) 100%)';
const BG_PEDIDO_DEFAULT   = 'linear-gradient(135deg, rgba(234,88,12,0.97) 0%, rgba(190,18,60,0.97) 100%)';

// ── Sub-componentes de overlay ────────────────────────────────────────────────

// ── Nível 2 (Informativo): Playlist automática tocando ────────────────────────
// Borda emerald diferencia visualmente da notificação de pedido de cliente.
export function NowPlayingToast({ item, saindo, topClass = 'top-6' }: { item: { titulo: string; thumbnail: string }; saindo?: boolean; topClass?: string }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      className={`absolute ${topClass} left-1/2 -translate-x-1/2 z-10 pointer-events-none`}
      initial={{ y: -12, opacity: 0 }}
      animate={saindo ? { y: -12, opacity: 0 } : { y: 0, opacity: 1 }}
      transition={{ duration: reduceMotion ? 0 : 0.3 }}
    >
      <div
        className="flex items-center gap-4 rounded-2xl px-6 py-4 bg-[rgba(6,8,16,0.78)] backdrop-blur-md border border-emerald-300/[0.28]"
        style={{ boxShadow: SHADOW_NOW_PLAYING }}
      >
        <div className="relative shrink-0 rounded-xl overflow-hidden ring-1 ring-emerald-400/30 w-[clamp(44px,3.2vw,64px)] h-[clamp(44px,3.2vw,64px)]">
          <img src={item.thumbnail} alt={item.titulo} className="w-full h-full object-cover"
            onError={(e) => { const t = e.target as HTMLImageElement; t.style.display = 'none'; if (t.parentElement) t.parentElement.style.background = 'linear-gradient(135deg,#1e293b,#0f172a)'; }}
          />
          <div className="absolute inset-0 bg-black/40 flex items-end justify-center pb-1 gap-[2px] pointer-events-none">
            {([0.4, 0.7, 0.5, 0.9, 0.6] as const).map((h, i) => (
              <motion.div
                key={i}
                className="w-[3px] rounded-full bg-emerald-300"
                style={{ height: `${h * 100}%` }}
                animate={reduceMotion ? undefined : { height: ['20%', '80%', '40%', '100%', '30%'] }}
                transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.08, ease: 'easeInOut' }}
              />
            ))}
          </div>
        </div>
        <div className="min-w-0">
          <p className="text-emerald-400/80 font-semibold uppercase tracking-widest leading-none mb-1.5 flex items-center gap-1.5 text-[clamp(10px,0.8vw,14px)]">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block shrink-0" />
            Tocando agora
          </p>
          <p className="text-white font-bold leading-snug line-clamp-1 text-[clamp(14px,1.3vw,22px)] max-w-[clamp(200px,28vw,520px)]">
            {item.titulo}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ── Nível 1 (Urgente): Pedido de cliente começando a tocar ───────────────────
// Banner de maior impacto visual — gradiente orange/rose, tamanho ampliado.
export function PedidoPopup({ pedido, saindo, batalhaAtiva }: { pedido: FilaJukebox; saindo?: boolean; batalhaAtiva?: boolean }) {
  const reduceMotion = useReducedMotion();
  const vibe = batalhaAtiva ? (pedido.vibe as 'romance' | 'sofrencia' | undefined) : undefined;
  const isRomance   = vibe === 'romance';
  const isSofrencia = vibe === 'sofrencia';
  const temVibe     = isRomance || isSofrencia;
  const primeiroNome = pedido.is_invasao_tematica
    ? null
    : getClientePrimeiroNome(pedido.cliente_nome);

  const bg   = isRomance ? BG_PEDIDO_ROMANCE   : isSofrencia ? BG_PEDIDO_SOFRENCIA   : BG_PEDIDO_DEFAULT;
  const glow = isRomance ? SHADOW_PEDIDO_ROMANCE : isSofrencia ? SHADOW_PEDIDO_SOFRENCIA : SHADOW_PEDIDO_DEFAULT;

  return (
    <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-none">
      <motion.div
        className="relative mx-auto mb-8 rounded-3xl overflow-hidden backdrop-blur-xl w-[min(48vw,760px)]"
        initial={{ y: 20, opacity: 0 }}
        animate={saindo ? { y: 20, opacity: 0 } : { y: 0, opacity: 1 }}
        transition={reduceMotion ? { duration: 0 } : { type: 'spring', damping: 30, stiffness: 300 }}
        style={{ background: bg, boxShadow: glow }}
      >
        {/* Light sweep — entra uma vez ao montar */}
        {!saindo && !reduceMotion && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
            <motion.div
              className="absolute left-0 -top-4 -bottom-4 w-1/3 bg-gradient-to-r from-transparent via-white/[0.07] to-transparent"
              initial={{ x: '-100%' }}
              animate={{ x: '200%' }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
            />
          </div>
        )}
        {/* Watermark de vibe — ícone gigante semitransparente no fundo */}
        {temVibe && (
          <div
            className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none select-none opacity-[0.10] text-white"
            style={{ width: 'clamp(52px,6vw,96px)', height: 'clamp(52px,6vw,96px)' }}
            aria-hidden
          >
            {isRomance
              ? <Heart className="w-full h-full" fill="currentColor" strokeWidth={0} />
              : <HeartCrack className="w-full h-full" fill="currentColor" strokeWidth={0} />
            }
          </div>
        )}

        <div className="flex items-center gap-5 px-6 py-5">
          {/* Thumbnail com equalizador animado */}
          <div className="relative shrink-0 rounded-2xl overflow-hidden ring-2 ring-white/30 w-[clamp(64px,5.5vw,96px)] h-[clamp(64px,5.5vw,96px)]">
            <img src={pedido.thumbnail} alt={pedido.titulo} className="w-full h-full object-cover"
              onError={(e) => { const t = e.target as HTMLImageElement; t.style.display = 'none'; if (t.parentElement) t.parentElement.style.background = 'linear-gradient(135deg,#1e293b,#0f172a)'; }}
            />
            <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
              <div className="flex gap-[3px] items-end h-[clamp(18px,2vw,32px)]">
                {[0.5, 0.8, 0.6, 1.0, 0.7].map((h, i) => (
                  <motion.div
                    key={i}
                    className="bg-white rounded-full w-[clamp(3px,0.3vw,5px)]"
                    style={{ height: `${h * 100}%` }}
                    animate={reduceMotion ? undefined : { height: ['20%', '80%', '40%', '100%', '30%'] }}
                    transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.08, ease: 'easeInOut' }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Texto */}
          <div className="flex-1 min-w-0">
            <p className="font-bold uppercase tracking-widest mb-1.5 flex items-center gap-2 flex-wrap text-white/80 text-[clamp(9px,0.8vw,13px)]">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse inline-block shrink-0" />
              {primeiroNome && (
                <TvClienteBadge
                  nome={pedido.cliente_nome}
                  avatarUrl={pedido.cliente_avatar_url}
                  size="sm"
                  showNome={false}
                />
              )}
              {temVibe ? (
                <>
                  {isRomance
                    ? <Heart size={10} fill="currentColor" strokeWidth={0} className="text-rose-300 shrink-0" />
                    : <HeartCrack size={10} fill="currentColor" strokeWidth={0} className="text-sky-300 shrink-0" />
                  }
                  {isRomance ? 'Time Apaixonado' : 'Time Sofrendo'}
                  <span className="text-white/55 font-normal normal-case tracking-normal">· pedido tocando agora</span>
                </>
              ) : primeiroNome ? (
                <span className="normal-case tracking-normal">
                  <span className="text-white font-semibold">{primeiroNome}</span>
                  <span className="text-white/50"> pediu esta música</span>
                </span>
              ) : (
                'Pedido do cliente tocando agora'
              )}
            </p>
            <p className="text-white font-black leading-tight line-clamp-2 text-[clamp(16px,1.9vw,32px)]">
              {pedido.titulo}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ── Nível 3 (Sutil): Notificação da fila (via useNotifQueue) ─────────────────
// Pill compacto no canto inferior direito — não compete com PedidoPopup.
// Recebe NotifAtiva para exibir contexto de fila (ex: "2 na fila") no futuro.
export function FilaNotifToast({ notif }: { notif: NotifAtiva }) {
  const reduceMotion = useReducedMotion();
  const isFilaAdicionada = notif.tipo === 'fila_adicionada';
  const primeiroNome = getClientePrimeiroNome(notif.clienteNome);
  return (
    <motion.div
      className="absolute bottom-6 right-6 z-10 pointer-events-none"
      initial={{ y: 8, opacity: 0 }}
      animate={notif.saindo ? { y: 8, opacity: 0 } : { y: 0, opacity: 1 }}
      transition={{ duration: reduceMotion ? 0 : 0.25 }}
    >
      <div
        className={`flex items-center gap-3 rounded-2xl px-4 py-3 bg-[rgba(8,10,20,0.82)] backdrop-blur-sm max-w-[clamp(220px,26vw,440px)] border ${isFilaAdicionada ? 'border-[rgba(251,146,60,0.22)]' : 'border-white/10'}`}
        style={{ boxShadow: SHADOW_FILA_NOTIF }}
      >
        {/* Avatar do cliente ou ícone de música */}
        {primeiroNome ? (
          <TvClienteBadge
            nome={notif.clienteNome}
            avatarUrl={notif.clienteAvatarUrl}
            size="md"
            showNome={false}
          />
        ) : (
          <div className={`shrink-0 flex items-center justify-center rounded-xl w-[clamp(32px,2.4vw,44px)] h-[clamp(32px,2.4vw,44px)] ${isFilaAdicionada ? 'bg-[rgba(251,146,60,0.15)]' : 'bg-[rgba(52,211,153,0.12)]'}`}>
            {isFilaAdicionada ? (
              <svg viewBox="0 0 24 24" fill="currentColor" className="text-orange-400 w-[clamp(14px,1.1vw,20px)] h-[clamp(14px,1.1vw,20px)]">
                <path d="M9 3v10.55A4 4 0 1 0 11 17V7h4V3H9z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" className="text-emerald-400 w-[clamp(14px,1.1vw,20px)] h-[clamp(14px,1.1vw,20px)]">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </div>
        )}

        {/* Thumbnail pequena */}
        <div className="shrink-0 rounded-lg overflow-hidden ring-1 ring-white/10 w-[clamp(34px,2.6vw,46px)] h-[clamp(34px,2.6vw,46px)]">
          <img src={notif.thumbnail} alt={notif.titulo} className="w-full h-full object-cover"
            onError={(e) => { const t = e.target as HTMLImageElement; t.style.display = 'none'; if (t.parentElement) t.parentElement.style.background = 'linear-gradient(135deg,#1e293b,#0f172a)'; }}
          />
        </div>

        {/* Texto */}
        <div className="min-w-0">
          <p className={`font-semibold uppercase tracking-wider leading-none mb-1 text-[clamp(9px,0.7vw,12px)] ${isFilaAdicionada ? 'text-orange-400/80' : 'text-emerald-400/80'}`}>
            {isFilaAdicionada
              ? (primeiroNome ? `${primeiroNome} adicionou à fila` : 'Adicionada à fila')
              : 'Tocando agora'}
          </p>
          <p className="text-white font-bold leading-snug line-clamp-1 text-[clamp(12px,1.0vw,18px)]">
            {notif.titulo}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export function FlashOverlay({ flash }: { flash: { text: string; style: 'modal' | 'ticker' } | null }) {
  const reduceMotion = useReducedMotion();
  if (!flash) return null;
  if (flash.style === 'modal') {
    return (
      <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
        <div className="rounded-3xl px-10 py-8 text-center bg-[rgba(0,0,0,0.75)] backdrop-blur-[20px] border border-white/15 max-w-[50vw]">
          <p className="text-white font-bold text-[3vw]">{flash.text}</p>
        </div>
      </div>
    );
  }
  if (reduceMotion) {
    return (
      <div className="absolute bottom-0 inset-x-0 z-20 pointer-events-none overflow-hidden bg-[rgba(0,0,0,0.85)] border-t border-white/10 py-[0.8vw]">
        <p className="text-white font-semibold text-center text-[2vw] truncate px-4">{flash.text}</p>
      </div>
    );
  }
  return (
    <div className="absolute bottom-0 inset-x-0 z-20 pointer-events-none overflow-hidden bg-[rgba(0,0,0,0.85)] border-t border-white/10 py-[0.8vw]">
      <motion.p
        className="text-white font-semibold whitespace-nowrap text-[2vw]"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
      >
        &nbsp;&nbsp;&nbsp;&nbsp;{flash.text}&nbsp;&nbsp;&nbsp;&nbsp;{flash.text}
      </motion.p>
    </div>
  );
}

// ── Botão Voltar reutilizável ──────────────────────────────────────────────────

function BotaoVoltar({ showControls, onVoltar }: { showControls: boolean; onVoltar: () => void }) {
  return (
    <div
      className="absolute top-0 left-0 z-50 p-4 pointer-events-none transition-opacity duration-[400ms] ease-[ease]"
      style={{ opacity: showControls ? 1 : 0 }}
    >
      <button
        onClick={onVoltar}
        tabIndex={showControls ? 0 : -1}
        className="pointer-events-auto flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold select-none bg-[rgba(0,0,0,0.65)] backdrop-blur-[16px] border border-white/15 transition-transform duration-100 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
        style={{ boxShadow: SHADOW_BOTAO_VOLTAR }}
      >
        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Voltar
      </button>
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface PlayerContentProps {
  tela:           Tela;
  telaId:         string;
  midias:         Midia[];
  activeMidias:   Midia[];
  currentIndex:   number;
  origemMidia:    FonteReproducao;
  audioCaptura:   'playlist' | 'captura';
  playlistItems:  PlaylistItem[];
  ytIndex:        number;
  playNonce:      number;
  pedidoAtual:    FilaJukebox | null;
  playerConfig:   PlayerConfig;
  qrVisible:      boolean;
  hypeQrDestaque?: boolean;
  hypeTexto?:      string;
  flashMessage:   { text: string; style: 'modal' | 'ticker'; duration: number } | null;
  nowPlaying:     { titulo: string; thumbnail: string } | null;
  nowPlayingSaindo: boolean;
  showPedidoPopup: FilaJukebox | null;
  pedidoPopupSaindo: boolean;
  /** Notificação ativa da fila FIFO (substitui filaNotif) */
  notifFilaAtiva: NotifAtiva | null;
  /** Raio-x da música tocando; null quando não há ou não foi encontrado. */
  raioX:        MusicaRaioX | null;
  raioXVisivel: boolean;
  /** PDV Engajamento: card "saiu um prato" (null = escondido) e termômetro do chopp (null = integração inativa). */
  pdvDestaque:   PdvDestaqueItem[] | null;
  pdvTermometro: TvChoppTermometroProps | null;
  /** Resultado da eleição (TSE); null = escondido (desligado, nunca carregou ou cedendo a outro overlay). */
  eleicao: EleicaoSnapshot | null;
  isOnline:       boolean;
  showControls:   boolean;
  logoAtiva:      string;
  adAtivo:        boolean;
  adMidiasRef:    RefObject<Midia[]>;
  blobMapRef:     RefObject<Map<string, string>>;
  partidaAtiva:   FutebolPartidaAtiva | null;
  ytUnlocked:     boolean;
  ytSlideRef:        RefObject<YoutubeSlideHandle | null>;
  ytLiveRef:         RefObject<YoutubeSlideHandle | null>;
  transmissaoVideoId: string | null;
  transmissaoTitulo:  string;
  captureSlideRef:   RefObject<CaptureSlideHandle | null>;
  windowCaptureRef:  RefObject<WindowCaptureHandle | null>;
  hdmiDispositivos:  DispositivoVideoCaptura[];
  hdmiDeviceIdAtivo: string;
  onSelecionarHdmi:  (deviceId: string) => void;
  videoRef:          RefObject<HTMLVideoElement | null>;
  playerVolumeRef:   RefObject<number>;
  playerMutedRef:    RefObject<boolean>;
  ganhoCaptura:      number;
  perfilAudio:       PerfilAudio;
  handleRequestFullscreen: () => void;
  handleMouseMove:         () => void;
  handleVoltar:            () => void;
  handleYtEnded:           () => Promise<void>;
  handleLiveEnded:         () => void;
  handleYtVideoError:      (code: number) => void;
  handleLiveVideoError:    (code: number) => void;
  handleAdCicloCompleto:   () => void;
  /** Barra das campanhas da plataforma; nunca abre junto com a do bar (useAdSidebar `bloqueada`). */
  campanhaBarraAtiva:          boolean;
  campanhaBarraMidiasRef:      RefObject<Midia[]>;
  handleCampanhaCicloCompleto: () => void;
  onYtUnlock:              () => void;
  onJanelaStreamActiveChange?: (active: boolean) => void;
}

// ── SlideDots — indicador de progresso do slideshow ──────────────────────────
function SlideDots({ total, active }: { total: number; active: number }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className={`h-0.5 rounded-full transition-all duration-300 ${i === active ? 'w-6 bg-white/90' : 'w-3 bg-white/25'}`} />
      ))}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

function PlayerContent({
  tela, telaId, midias: _midias, activeMidias, currentIndex,
  origemMidia, audioCaptura, playlistItems, ytIndex, playNonce, pedidoAtual,
  playerConfig, qrVisible, hypeQrDestaque = false, hypeTexto = '', flashMessage,
  nowPlaying, nowPlayingSaindo,
  showPedidoPopup, pedidoPopupSaindo,
  notifFilaAtiva,
  raioX, raioXVisivel, pdvDestaque, pdvTermometro, eleicao,
  isOnline, showControls, logoAtiva, adAtivo, adMidiasRef,
  blobMapRef, partidaAtiva, ytUnlocked, ytSlideRef, ytLiveRef,
  transmissaoVideoId, transmissaoTitulo, captureSlideRef, windowCaptureRef,
  hdmiDispositivos, hdmiDeviceIdAtivo, onSelecionarHdmi,
  videoRef,
  playerVolumeRef, playerMutedRef, ganhoCaptura, perfilAudio,
  handleRequestFullscreen, handleMouseMove, handleVoltar,
  handleYtEnded, handleLiveEnded, handleYtVideoError, handleLiveVideoError,
  handleAdCicloCompleto, campanhaBarraAtiva, campanhaBarraMidiasRef, handleCampanhaCicloCompleto, onYtUnlock,
  onJanelaStreamActiveChange,
}: PlayerContentProps) {
  const reduceMotion  = useReducedMotion();
  const isCaptura     = origemMidia === 'captura' || origemMidia === 'captura_janela';
  const isTransmissao = origemMidia === 'transmissao';
  // Faixa da eleição: presente em todos os modos; sobe com o logo quando o RAIO-X aparece.
  const eleicaoFaixa = eleicao ? <TvResultadoEleicao snapshot={eleicao} elevado={raioXVisivel} /> : null;
  const offScreenStyle = { top: '-9999px', left: '-9999px', width: '1px', height: '1px', pointerEvents: 'none' as const };
  const fullScreenStyle = { inset: 0 };

  // ── INVARIANTE DE DOM do YoutubeSlide (ver CLAUDE.md) ────────────────────
  // <YoutubeSlide ref={ytSlideRef}> precisa ocupar sempre a mesma posição no
  // retorno do componente, independente de qual "modo" (playlist/captura/
  // slideshow) está ativo — senão o YT.Player é destruído e recriado a cada
  // troca de branch (ex: admin remove a playlist em runtime), gerando flood
  // de erros de postMessage. Por isso o cálculo de faixaAtual e o próprio
  // <YoutubeSlide> ficam fora dos branches abaixo (que só decidem o que
  // aparece ao redor dele) e são sempre renderizados no fragment final.
  const faixaAtual = resolverFaixaAtual(pedidoAtual, playlistItems[ytIndex] ?? playlistItems[0]);

  const playlistVazia = !!tela.playlist_id && playlistItems.length === 0 && !pedidoAtual;
  const ytFullscreenVisible = !!tela.playlist_id && !isCaptura && !isTransmissao && !playlistVazia;
  const ytWrapper = (
    <div
      className="fixed z-0"
      style={ytFullscreenVisible
        ? { inset: 0, right: adAtivo ? '25%' : 0 }
        : offScreenStyle}
      aria-hidden={!ytFullscreenVisible}
    >
      <YoutubeSlide
        ref={ytSlideRef}
        videoId={faixaAtual?.videoId ?? ''}
        titulo={faixaAtual?.titulo ?? ''}
        playKey={pedidoAtual ? pedidoAtual.id : `${playlistItems[ytIndex]?.id ?? ytIndex}:${playNonce}`}
        duracaoSegundos={pedidoAtual ? null : (playlistItems[ytIndex]?.duracao_segundos ?? null)}
        onEnded={handleYtEnded}
        onVideoError={handleYtVideoError}
        unlocked={ytUnlocked}
        onUnlock={onYtUnlock}
        initialVolume={playerVolumeRef.current ?? 100}
        initialMuted={playerMutedRef.current ?? false}
      />
      {/* Impede o TV Box de promover o vídeo ao plano de overlay de hardware.
          Com capa estática o decodificador quase não produz quadros e, nessas
          caixas, a apresentação é dirigida pelo quadro de vídeo: a camada da
          página (logo, QR, HUD, ConnectionDot) nunca era mesclada e a tela
          ficava só com o vídeo. Nada dentro da página resgatava, por isso os
          dois keepalives de repintura falharam. Cobrir a área do vídeo com
          qualquer coisa não-opaca torna o plano de hardware inelegível e força
          composição normal da página. 1% de branco é imperceptível na TV;
          `pointer-events-none` para não roubar clique do player. */}
      <div
        aria-hidden
        data-testid="video-overlay-guard"
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundColor: 'rgba(255,255,255,0.01)' }}
      />
    </div>
  );

  // Mesmo invariante para a live: ytLiveWrapper sempre montado no return raiz
  // (irmão de ytWrapper), off-screen quando não há transmissão com videoId.
  const liveMuted = isTransmissao && audioCaptura === 'playlist';
  const liveFullscreenVisible = isTransmissao && !!transmissaoVideoId;
  const ytLiveWrapper = (
    <div
      className="fixed z-0"
      style={liveFullscreenVisible ? fullScreenStyle : offScreenStyle}
      aria-hidden={!liveFullscreenVisible}
    >
      <YoutubeSlide
        ref={ytLiveRef}
        videoId={transmissaoVideoId ?? ''}
        titulo={transmissaoTitulo}
        modoLive
        onEnded={handleLiveEnded}
        onVideoError={handleLiveVideoError}
        unlocked={ytUnlocked}
        onUnlock={onYtUnlock}
        initialVolume={playerVolumeRef.current ?? 100}
        initialMuted={liveMuted || (playerMutedRef.current ?? false)}
      />
    </div>
  );

  // ── Dip-to-black transition state (slideshow only) ───────────────────────
  const [displayIndex, setDisplayIndex] = useState(currentIndex);
  const [transPhase,   setTransPhase]   = useState<'idle' | 'in' | 'out'>('idle');
  const transTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (tela.playlist_id || isCaptura || activeMidias.length === 0) return;
    if (currentIndex === displayIndex) return;
    if (transTimerRef.current) clearTimeout(transTimerRef.current);
    setTransPhase('in');
    transTimerRef.current = setTimeout(() => {
      setDisplayIndex(currentIndex);
      setTransPhase('out');
      transTimerRef.current = setTimeout(() => setTransPhase('idle'), 160);
    }, 150);
    return () => { if (transTimerRef.current) clearTimeout(transTimerRef.current); };
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Conteúdo por modo (playlist / captura / slideshow) ───────────────────
  // Os <YoutubeSlide> (playlist + live) já foram hoisted para fora deste
  // bloco (ytWrapper / ytLiveWrapper) — os branches abaixo só decidem o
  // que aparece ao redor deles.
  const conteudo = (() => {
  if (tela.playlist_id) {
    return (
      <div className={`fixed inset-0 flex overflow-hidden ${ytFullscreenVisible ? 'pointer-events-none' : 'bg-zinc-950'}`} onMouseMove={handleMouseMove}>

        {playlistVazia && <PlaylistVaziaScreen tela={tela} isOnline={isOnline} />}

        <BotaoVoltar showControls={showControls} onVoltar={handleVoltar} />

        <div className={`relative h-full overflow-hidden flex-1 ${ytFullscreenVisible ? 'pointer-events-none' : ''}`}>
          {origemMidia === 'captura' && (
            <CaptureSlide
              ref={captureSlideRef}
              muted={audioCaptura === 'playlist'}
              ganho={ganhoCaptura}
              perfil={perfilAudio}
              deviceIdAtivo={hdmiDeviceIdAtivo}
              dispositivos={hdmiDispositivos}
              onSelecionarDispositivo={onSelecionarHdmi}
            />
          )}
          {origemMidia === 'captura_janela' && (
            <WindowCaptureSlide
              ref={windowCaptureRef}
              muted={audioCaptura === 'playlist'}
              perfil={perfilAudio}
              onStreamActiveChange={onJanelaStreamActiveChange}
            />
          )}

          {isTransmissao && !transmissaoVideoId && (
            <div
              className="absolute"
              style={fullScreenStyle}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-950 gap-3">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <p className="text-white font-semibold text-lg">{transmissaoTitulo}</p>
                <p className="text-slate-500 text-sm">Configure o ID da live no roster de transmissores</p>
              </div>
            </div>
          )}

          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            <ConnectionDot isOnline={isOnline} />
          </div>

          {(qrVisible || hypeQrDestaque) && (
            <motion.div
              className="absolute top-6 left-6 z-20 origin-top-left"
              animate={{
                scale: hypeQrDestaque ? 1.45 : 1,
              }}
              transition={{ type: 'spring', stiffness: 220, damping: 22 }}
            >
              <JukeboxQRCode
                telaId={telaId}
                isCaptura={isCaptura || isTransmissao}
                temJogoAtivo={!!partidaAtiva?.fixture_id}
                destaque={hypeQrDestaque}
                ctaTexto={hypeTexto}
              />
            </motion.div>
          )}

          {pedidoAtual && (
            <div className="absolute top-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 rounded-2xl px-5 py-2.5 bg-[rgba(0,0,0,0.60)] backdrop-blur-[16px] border border-[rgba(251,146,60,0.35)] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
              <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse shrink-0" />
              {!pedidoAtual.is_invasao_tematica && getClientePrimeiroNome(pedidoAtual.cliente_nome) ? (
                <TvClienteBadge
                  nome={pedidoAtual.cliente_nome}
                  avatarUrl={pedidoAtual.cliente_avatar_url}
                  size="sm"
                  nomeClassName="text-white font-semibold text-[1.1vw]"
                />
              ) : (
                <span className="text-white font-semibold text-[1.1vw]">Pedido do cliente</span>
              )}
              <div className="w-px h-4 bg-white/20 mx-1" />
              <span className="text-orange-300 font-semibold line-clamp-1 text-[1.1vw] max-w-[25vw]">{pedidoAtual.titulo}</span>
            </div>
          )}

          {/* Nível 2 — Tocando agora (playlist automática): posicionado abaixo do QR Code para evitar colisão */}
          {nowPlaying && !showPedidoPopup && (
            <NowPlayingToast
              item={nowPlaying}
              saindo={nowPlayingSaindo}
              topClass={(qrVisible || hypeQrDestaque) ? 'top-[clamp(100px,8vw,150px)]' : 'top-6'}
            />
          )}

          <div
            className={`absolute bottom-6 left-6 z-10 pointer-events-none transition-transform duration-300 ${
              raioXVisivel ? '-translate-y-[clamp(96px,9vw,150px)]' : ''
            }`}
          >
            <img src={logoAtiva} alt={`Logo ${tela.nome}`} className="h-[clamp(48px,5vw,120px)] w-auto object-contain drop-shadow-2xl" />
          </div>

          {raioX && <TvRaioXBar dados={raioX} visivel={raioXVisivel} />}
          {pdvTermometro && !isCaptura && !isTransmissao && <TvChoppTermometro {...pdvTermometro} />}
          {!isCaptura && !isTransmissao && <TvPdvDestaqueCard itens={pdvDestaque} />}
          {eleicaoFaixa}

          <FlashOverlay flash={flashMessage} />
          {/* Nível 1 — Pedido do cliente: maior prioridade visual */}
          {showPedidoPopup && <PedidoPopup pedido={showPedidoPopup} saindo={pedidoPopupSaindo} batalhaAtiva={playerConfig.config_batalha_vibes} />}
          {/* Nível 3 — Fila FIFO: suprimida quando PedidoPopup está ativo */}
          {notifFilaAtiva && !showPedidoPopup && <FilaNotifToast notif={notifFilaAtiva} />}
        </div>

        {!isCaptura && !isTransmissao && (
          <div className="h-screen overflow-hidden shrink-0" data-testid="coluna-anuncios"
            style={{ width: adAtivo || campanhaBarraAtiva ? '25%' : '0%', transition: reduceMotion ? 'none' : 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
            {adAtivo
              ? <AdsSidebar midias={adMidiasRef.current ?? []} blobMap={blobMapRef.current ?? new Map()} onCicloCompleto={handleAdCicloCompleto} />
              : campanhaBarraAtiva && (
                <AdsSidebar key="plataforma" midias={campanhaBarraMidiasRef.current ?? []} blobMap={blobMapRef.current ?? new Map()} onCicloCompleto={handleCampanhaCicloCompleto} />
              )}
          </div>
        )}

        {(isCaptura || isTransmissao) && playerConfig.exibir_futebol && partidaAtiva?.fixture_id && (
          <TvFutebolOverlay telaId={telaId} fixtureId={partidaAtiva.fixture_id} />
        )}
        <TvReacaoOverlay telaId={telaId} />
      </div>
    );
  }

  // ── Modo Captura / Transmissão sem playlist ────────────────────────────────
  if (origemMidia !== 'playlist' && origemMidia !== 'transmissao') {
    return (
      <div className="fixed inset-0 bg-zinc-950 flex overflow-hidden" onMouseMove={handleMouseMove}>

        <BotaoVoltar showControls={showControls} onVoltar={handleVoltar} />

        <div className="relative flex-1 h-full overflow-hidden">
          {origemMidia === 'captura' && (
            <CaptureSlide
              ref={captureSlideRef}
              muted={audioCaptura === 'playlist'}
              ganho={ganhoCaptura}
              perfil={perfilAudio}
              deviceIdAtivo={hdmiDeviceIdAtivo}
              dispositivos={hdmiDispositivos}
              onSelecionarDispositivo={onSelecionarHdmi}
            />
          )}
          {origemMidia === 'captura_janela' && (
            <WindowCaptureSlide
              ref={windowCaptureRef}
              muted={audioCaptura === 'playlist'}
              perfil={perfilAudio}
              onStreamActiveChange={onJanelaStreamActiveChange}
            />
          )}

          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            <ConnectionDot isOnline={isOnline} />
          </div>

          {nowPlaying && !showPedidoPopup && <NowPlayingToast item={nowPlaying} saindo={nowPlayingSaindo} />}

          <div className="absolute bottom-6 left-6 z-10 pointer-events-none">
            <img src={logoAtiva} alt={`Logo ${tela.nome}`} className="h-[clamp(48px,5vw,120px)] w-auto object-contain drop-shadow-2xl" />
          </div>

          {eleicaoFaixa}
          <FlashOverlay flash={flashMessage} />
        </div>

        {playerConfig.exibir_futebol && partidaAtiva?.fixture_id && (
          <TvFutebolOverlay telaId={telaId} fixtureId={partidaAtiva.fixture_id} />
        )}
        <TvReacaoOverlay telaId={telaId} />
      </div>
    );
  }

  // ── Modo slideshow de mídias ─────────────────────────────────────────────

  if (activeMidias.length === 0 && _midias.length === 0) {
    return (
      <div className="fixed inset-0 bg-zinc-950 flex items-center justify-center cursor-pointer" onClick={handleRequestFullscreen}>
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-slate-800 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-white text-xl font-semibold">{tela.nome}</h2>
          <p className="text-slate-500 text-sm mt-2">Aguardando mídias...</p>
          <div className="mt-6 flex items-center justify-center gap-2"><ConnectionDot isOnline={isOnline} /></div>
        </div>
      </div>
    );
  }

  if (activeMidias.length === 0) return <StandbyScreen tela={tela} isOnline={isOnline} />;

  const currentMidia  = activeMidias[displayIndex] ?? activeMidias[0];
  const localSrc      = blobMapRef.current?.get(currentMidia.url);
  const isLBar        = playerConfig.layout_template === 'l-bar' || tela.layout_atual === 'l-bar';
  const nextSlideIndex = activeMidias.length > 1 ? (displayIndex + 1) % activeMidias.length : -1;
  const nextMidia     = nextSlideIndex >= 0 ? activeMidias[nextSlideIndex] : null;
  const nextLocalSrc  = nextMidia ? blobMapRef.current?.get(nextMidia.url) : undefined;

  if (!isLBar) {
    return (
      <div className="fixed inset-0 bg-zinc-950 overflow-hidden">
        <MediaSlide key={`${currentMidia.id}-${displayIndex}`} midia={currentMidia} localSrc={localSrc}
          videoRef={currentMidia.tipo === 'video' ? videoRef : undefined} />
        {nextMidia?.tipo === 'video' && (
          <div className="absolute -top-[9999px] -left-[9999px] w-px h-px overflow-hidden pointer-events-none" aria-hidden>
            <MediaSlide key={`pre-${nextMidia.id}-${nextSlideIndex}`} midia={nextMidia} localSrc={nextLocalSrc} />
          </div>
        )}
        <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
          <SlideDots total={activeMidias.length} active={displayIndex} />
          <ConnectionDot isOnline={isOnline} />
        </div>
        {eleicaoFaixa}
        {transPhase !== 'idle' && (
          <motion.div
            className="absolute inset-0 z-30 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: transPhase === 'in' ? 1 : 0 }}
            transition={{ duration: 0.15 }}
            style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(6,8,16,0.98) 100%)' }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-zinc-950 overflow-hidden relative">
      <MediaSlide key={`${currentMidia.id}-${displayIndex}`} midia={currentMidia} localSrc={localSrc}
        videoRef={currentMidia.tipo === 'video' ? videoRef : undefined} />
      {nextMidia?.tipo === 'video' && (
        <div className="absolute -top-[9999px] -left-[9999px] w-px h-px overflow-hidden pointer-events-none" aria-hidden>
          <MediaSlide key={`pre-${nextMidia.id}-${nextSlideIndex}`} midia={nextMidia} localSrc={nextLocalSrc} />
        </div>
      )}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <SlideDots total={activeMidias.length} active={displayIndex} />
        <ConnectionDot isOnline={isOnline} />
      </div>
      {eleicaoFaixa}
      {transPhase !== 'idle' && (
        <motion.div
          className="absolute inset-0 z-30 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: transPhase === 'in' ? 1 : 0 }}
          transition={{ duration: 0.15 }}
          style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(6,8,16,0.98) 100%)' }}
        />
      )}
    </div>
  );
  })();

  return (
    <>
      {ytWrapper}
      {ytLiveWrapper}
      {conteudo}
    </>
  );
}

export default memo(PlayerContent);
